import React, { useEffect, useState, useCallback } from "react";
import { TableEmpty } from "@finance/components/DesignSystem";
import { api, fmtDate, fmtNum, errMsg } from "@finance/lib/api";
import Layout, { PageHeader } from "@finance/components/Layout";
import { useAuth } from "@finance/context/AuthContext";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { Label } from "@finance/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@finance/components/ui/dialog";
import { toast } from "sonner";
import { Lock, LockOpen, Prohibit, Scales } from "@phosphor-icons/react";
import { refreshPeriodCache } from "@finance/components/LockedPeriodBanner";

const ym = () => new Date().toISOString().slice(0, 7);
const GL = (n) => (n > 0 ? "text-emerald-600" : n < 0 ? "text-red-600" : "text-gray-500");
const Badge = ({ status }) => {
  const cls = status === "closed" ? "bg-gray-900 text-white" : status === "locked" ? "bg-amber-100 text-amber-700" : "bg-emerald-50 text-emerald-700";
  return <span className={`rounded-sm px-2 py-0.5 text-xs font-medium ${cls}`}>{status || "open"}</span>;
};

export default function Periods() {
  const { user } = useAuth();
  const isOwner = !!user?.is_owner;
  const [rows, setRows] = useState([]);
  const [period, setPeriod] = useState(ym());
  const [closeDlg, setCloseDlg] = useState(null);   // { period, reval, override }
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/periods").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const act = async (action) => {
    try {
      await api.post(`/periods/${action}`, { period });
      refreshPeriodCache();
      toast.success(`Period ${period} ${action === "unlock" ? "unlocked" : "locked"}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const buildOverrides = (raw) => {
    const o = {};
    (raw || "").split(",").map((s) => s.trim()).filter(Boolean).forEach((pair) => {
      const [cur, rate] = pair.split(":").map((x) => x.trim());
      if (cur && rate && !Number.isNaN(Number(rate))) o[cur.toUpperCase()] = Number(rate);
    });
    return o;
  };

  const openClose = async () => {
    setBusy(true);
    try {
      const [pv, ck] = await Promise.all([
        api.post("/periods/close/preview", { period }),
        api.get(`/periods/close/checklist?period=${period}`),
      ]);
      setCloseDlg({ period, reval: pv.data, checklist: ck.data, override: "" });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const refreshClosePreview = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/periods/close/preview", { period: closeDlg.period, rate_overrides: buildOverrides(closeDlg.override) });
      setCloseDlg((d) => ({ ...d, reval: data }));
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const confirmClose = async () => {
    if (!window.confirm(`Close ${closeDlg.period} permanently? This posts the FX revaluation and PERMANENTLY seals the month — it can never be reopened.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/periods/close", { period: closeDlg.period, rate_overrides: buildOverrides(closeDlg.override) });
      refreshPeriodCache();
      const r = data.revaluation || {};
      toast.success(r.posted
        ? `Period ${closeDlg.period} closed. FX revaluation posted (net ${fmtNum(r.net_gain_loss)} TZS, reverses ${fmtDate(r.reversal_date)}).`
        : `Period ${closeDlg.period} closed. No FX exposure to revalue.`);
      setCloseDlg(null);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const reval = closeDlg?.reval;

  return (
    <Layout>
      <PageHeader title="Period Lock & Close" subtitle="Lock = reversible freeze. Close permanently = review FX revaluation, approve, post, and seal for good. Owner (Ronick) only." />
      <div className="p-8" data-testid="periods-page">
        {isOwner ? (
          <div className="mb-6 flex flex-wrap items-end gap-3 rounded-xl bg-white p-4 shadow-sm">
            <div>
              <Label className="lx-label">Period (month)</Label>
              <Input type="month" className="mt-1 w-48" data-testid="period-input" value={period} onChange={(e) => setPeriod(e.target.value)} />
            </div>
            <Button variant="outline" data-testid="lock-btn" onClick={() => act("lock")}><Lock size={16} className="mr-1" />Lock</Button>
            <Button variant="outline" data-testid="unlock-btn" onClick={() => act("unlock")}><LockOpen size={16} className="mr-1" />Unlock</Button>
            <Button className="bg-gray-900 hover:bg-gray-700 text-white" data-testid="close-btn" disabled={busy} onClick={openClose}><Prohibit size={16} className="mr-1" />Close permanently…</Button>
          </div>
        ) : (
          <div className="mb-6 rounded-sm border border-dashed border-gray-200 p-4 text-sm text-gray-500">Only Ronick can lock, unlock, or close periods. This is a read-only view.</div>
        )}

        <div className="overflow-hidden rounded-xl bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-gray-50"><tr className="lx-label border-b border-gray-100">
              <th className="px-4 py-2 text-left">Period</th><th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-left">Locked By</th><th className="px-4 py-2 text-left">Closed By</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <TableEmpty colSpan={4} title="All months are open" message="Locked and closed accounting periods will show up here." />}
              {rows.map((p) => (
                <tr key={p.period} data-testid={`period-row-${p.period}`} className="border-b border-gray-50">
                  <td className="px-4 py-2 font-mono-num font-medium">{p.period}</td>
                  <td className="px-4 py-2"><Badge status={p.status} /></td>
                  <td className="px-4 py-2 text-gray-500">{p.status !== "open" && p.locked_by ? `${p.locked_by} · ${fmtDate(p.locked_at)}` : "—"}</td>
                  <td className="px-4 py-2 text-gray-500">{p.closed_by ? `${p.closed_by} · ${fmtDate(p.closed_at)}` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!closeDlg} onOpenChange={(o) => !o && setCloseDlg(null)}>
        <DialogContent className="max-w-3xl" data-testid="close-reval-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Scales size={18} className="text-[#1B4383]" />Close {closeDlg?.period} permanently — review FX revaluation</DialogTitle>
          </DialogHeader>
          {reval && (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">
                Closing is a <b>one-way door</b> — the month can never be reopened. First review the unrealized FX revaluation below; on approval it posts
                (dated {fmtDate(reval.period_end)}) and reverses automatically on {fmtDate(reval.reversal_date)}. Nothing posts until you click <b>Approve &amp; Close permanently</b>.
              </p>

              {closeDlg.checklist && (
                <div className="grid grid-cols-3 gap-3" data-testid="close-checklist">
                  <div className="rounded-lg bg-gray-50 p-3">
                    <div className="lx-label text-gray-400">Unreconciled payments</div>
                    <div className={`mt-1 text-lg font-bold ${closeDlg.checklist.unreconciled_count ? "text-amber-600" : "text-emerald-600"}`} data-testid="checklist-unreconciled">{closeDlg.checklist.unreconciled_count}</div>
                    <div className="text-[11px] text-gray-400">TSh {fmtNum(closeDlg.checklist.unreconciled_amount)} this month</div>
                  </div>
                  <div className="rounded-lg bg-gray-50 p-3">
                    <div className="lx-label text-gray-400">Pending documents</div>
                    <div className={`mt-1 text-lg font-bold ${closeDlg.checklist.pending_docs_count ? "text-amber-600" : "text-emerald-600"}`} data-testid="checklist-pending-docs">{closeDlg.checklist.pending_docs_count}</div>
                    <div className="text-[11px] text-gray-400">unconfirmed uploads</div>
                  </div>
                  <div className="rounded-lg bg-gray-50 p-3">
                    <div className="lx-label text-gray-400">FX revaluation (net)</div>
                    <div className={`mt-1 text-lg font-bold ${GL(closeDlg.checklist.reval_net)}`} data-testid="checklist-reval">{fmtNum(closeDlg.checklist.reval_net, "TZS")}</div>
                    <div className="text-[11px] text-gray-400">{closeDlg.checklist.reval_rows} line(s)</div>
                  </div>
                </div>
              )}
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[220px]">
                  <Label className="lx-label">Manual closing rate override (optional)</Label>
                  <Input className="mt-1" value={closeDlg.override} data-testid="close-reval-override"
                    onChange={(e) => setCloseDlg((d) => ({ ...d, override: e.target.value }))} placeholder="USD:2650, EUR:2900" />
                </div>
                <Button variant="outline" onClick={refreshClosePreview} disabled={busy} data-testid="close-reval-recompute">Recompute</Button>
              </div>

              {reval.rows.length === 0 ? (
                <div className="rounded-lg bg-gray-50 py-6 text-center text-sm text-gray-400" data-testid="close-reval-none">No open foreign-currency balances — nothing to revalue. Closing will just seal the month.</div>
              ) : (
                <div className="max-h-72 overflow-auto rounded-lg border border-gray-100">
                  <table className="w-full text-sm">
                    <thead className="lx-label sticky top-0 bg-gray-50 text-gray-500">
                      <tr><th className="px-3 py-2 text-left">Account</th><th className="text-left">Cur</th>
                        <th className="text-right">FC bal</th><th className="text-right">Adv. offset</th><th className="text-right">Rate</th>
                        <th className="text-right">Booked</th><th className="text-right">Revalued</th><th className="px-3 text-right">Unrealized</th></tr>
                    </thead>
                    <tbody>
                      {reval.rows.map((r, i) => (
                        <tr key={i} className="border-t border-gray-50" data-testid={`close-reval-row-${i}`}>
                          <td className="px-3 py-1.5">{r.account_name} <span className="text-gray-400">{r.account_code}</span></td>
                          <td>{r.currency}</td>
                          <td className="text-right">{fmtNum(r.foreign_balance, r.currency)}</td>
                          <td className="text-right text-gray-400">{fmtNum(r.advance_offset_fc || 0, r.currency)}</td>
                          <td className="text-right">{fmtNum(r.rate)}<span className="ml-1 text-[10px] text-gray-400">{r.rate_source}</span></td>
                          <td className="text-right">{fmtNum(r.booked_tzs, "TZS")}</td>
                          <td className="text-right">{fmtNum(r.revalued_tzs, "TZS")}</td>
                          <td className={`px-3 text-right font-medium ${GL(r.unrealized_gain_loss)}`}>{fmtNum(r.unrealized_gain_loss, "TZS")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="text-sm">Net unrealized (gain +/loss −): <b className={GL(reval.net_gain_loss)} data-testid="close-reval-net">{fmtNum(reval.net_gain_loss, "TZS")}</b></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseDlg(null)} disabled={busy}>Cancel</Button>
            <Button className="bg-gray-900 text-white hover:bg-gray-700" onClick={confirmClose} disabled={busy} data-testid="close-reval-approve">
              <Prohibit size={15} className="mr-1" />{busy ? "Working…" : "Approve & Close permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
